/** Map API functions. */

import { apiGet, apiPost, apiPatch, apiDelete } from '@/api/client';
import type { MapListItem, MapDetail, CreateMapResponse } from '@/api/types';

export function createMap(title: string): Promise<CreateMapResponse> {
  return apiPost<CreateMapResponse>('/maps', { title });
}

export async function listMaps(): Promise<MapListItem[]> {
  const response = await apiGet<{ maps: MapListItem[] }>('/maps');
  return response.maps;
}

export async function getMap(mapId: string): Promise<MapDetail> {
  // Lambda returns { meta: { mapId, title, ... }, downloadUrl }
  const response = await apiGet<{
    meta: { mapId: string; title: string; updatedAt: string };
    downloadUrl: string;
  }>(`/maps/${mapId}`);
  return {
    mapId: response.meta.mapId,
    title: response.meta.title,
    downloadUrl: response.downloadUrl,
    uploadUrl: '', // Only available from create/update responses
    updatedAt: response.meta.updatedAt,
  };
}

export async function updateMapMeta(mapId: string, title: string): Promise<{ mapId: string; uploadUrl: string }> {
  // Lambda returns { mapId, uploadUrl }
  return apiPatch<{ mapId: string; uploadUrl: string }>(`/maps/${mapId}`, { title });
}

export function deleteMap(mapId: string): Promise<void> {
  return apiDelete(`/maps/${mapId}`);
}

/**
 * Upload the Yjs document state to an S3 pre-signed URL.
 * This bypasses the API client since it goes directly to S3.
 */
export async function uploadMapDocument(
  uploadUrl: string,
  yjsState: Uint8Array,
): Promise<void> {
  const res = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/octet-stream' },
    body: yjsState as unknown as BodyInit,
  });
  if (!res.ok) {
    throw new Error(`Upload failed: ${res.status} ${res.statusText}`);
  }
}

/**
 * Download the Yjs document state from an S3 pre-signed URL.
 * This bypasses the API client since it goes directly to S3.
 */
export async function downloadMapDocument(
  downloadUrl: string,
): Promise<Uint8Array> {
  const res = await fetch(downloadUrl);
  if (!res.ok) {
    throw new Error(`Download failed: ${res.status} ${res.statusText}`);
  }
  const buffer = await res.arrayBuffer();
  return new Uint8Array(buffer);
}
